import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import Card, { CardHeader } from '../../../components/ui/Card'
import Button from '../../../components/ui/Button'
import SearchableSelect from '../../../components/ui/SearchableSelect'
import Spinner from '../../../components/ui/Spinner'
import Modal from '../../../components/ui/Modal'
import DoctorSearchSelect from '../../../components/ui/DoctorSearchSelect'
import api from '../../../lib/api'
import { formatCurrency, formatDate } from '../../../lib/formatters'
import type { Doctor } from '../../../types'
import toast from 'react-hot-toast'

// Lab Profile response interface (matches API snake_case response)
interface LabProfileResponse {
  id?: string
  name?: string
  address_line1?: string
  address_line2?: string
  city?: string
  state?: string
  state_code?: string
  pincode?: string
  gstin?: string
  pan?: string
  phone?: string
  email?: string
  bank_name?: string
  account_holder_name?: string
  account_number?: string
  ifsc_code?: string
  account_type?: string
  category_code?: string
  upi_id?: string
  upi_qr_url?: string
  logo_url?: string
}

// ─── Types ───────────────────────────────────────────────────

interface StatementLineItem {
  date: string
  invoiceNumber: string
  billDate: string
  patientName: string
  productName: string
  notation: string
  units: number
  netAmount: number
  received: number
  balance: number
  workOrderId: string
  workOrderNumber: string  // Display ID for the work order (e.g., WO-001)
  workOrderDate: string    // Work order creation date
  invoiceId: string
}

interface DoctorMonthlyStatement {
  doctor: Doctor
  period: string
  previousDues: number
  currentMonthTotal: number
  currentMonthPaid: number
  totalDue: number
  items: StatementLineItem[]
  summary: {
    totalInvoiced: number
    totalPaid: number
    totalBalance: number
    invoiceCount: number
    workOrderCount: number
  }
}

interface BulkSendResult {
  sent: number
  failed: number
  errors: string[]
}

// ─── Helpers ─────────────────────────────────────────────────

const getPeriodOptions = () => {
  const options: Array<{ value: string; label: string; sublabel?: string }> = []
  const now = new Date()
  const currentYear = now.getFullYear()
  const currentMonth = now.getMonth()

  // Current month first
  for (let i = 0; i < 12; i++) {
    const date = new Date(currentYear, currentMonth - i, 1)
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
    const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    const isCurrentMonth = i === 0
    options.push({
      value,
      label,
      sublabel: isCurrentMonth ? 'Current month' : undefined,
    })
  }

  return options
}

const periodOptions = getPeriodOptions()

// FDI Tooth Numbering - International Standard
// Q1 Upper Right: 11-18, Q2 Upper Left: 21-28, Q3 Lower Left: 31-38, Q4 Lower Right: 41-48

// Teeth Quadrant Grid component for table display
function TeethQuadrantGrid({ notation }: { notation: string }) {
  if (!notation || notation === '-') return <span className="text-gray-400">-</span>

  const parts = notation.split(/[\s,]+/).map(s => s.trim()).filter(Boolean)
  if (parts.length === 0) return <span className="text-gray-400">-</span>

  // Accept FDI numbers (11-18, 21-28, 31-38, 41-48)
  const numbers = parts.map(p => parseInt(p, 10)).filter(n => !isNaN(n) && n >= 11 && n <= 48)
  if (numbers.length === 0) return <span className="text-gray-400 text-xs">{notation}</span>

  // Check if all teeth are selected (32 teeth)
  if (numbers.length >= 32) {
    return (
      <span
        className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-semibold rounded bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300"
        title="All 32 teeth"
      >
        All
      </span>
    )
  }

  // Parse into quadrants using FDI notation
  // Q1 Upper Right: 11-18 (display: 18,17,16,15,14,13,12,11 - descending)
  // Q2 Upper Left: 21-28 (display: 21,22,23,24,25,26,27,28 - ascending)
  // Q3 Lower Left: 31-38 (display: 31,32,33,34,35,36,37,38 - ascending)
  // Q4 Lower Right: 41-48 (display: 48,47,46,45,44,43,42,41 - descending)
  const q1: number[] = [] // Upper Right: FDI 11-18
  const q2: number[] = [] // Upper Left: FDI 21-28
  const q3: number[] = [] // Lower Left: FDI 31-38
  const q4: number[] = [] // Lower Right: FDI 41-48

  for (const n of numbers) {
    if (n >= 11 && n <= 18) q1.push(n)
    else if (n >= 21 && n <= 28) q2.push(n)
    else if (n >= 31 && n <= 38) q3.push(n)
    else if (n >= 41 && n <= 48) q4.push(n)
  }

  const hasAny = q1.length || q2.length || q3.length || q4.length
  if (!hasAny) return <span className="text-gray-400 text-xs">{notation}</span>

  // Sort for display (anatomical order from patient's right to left):
  // Q1: descending (18,17,16...) - right side, from back to front
  // Q2: ascending (21,22,23...) - left side, from front to back
  // Q3: ascending (31,32,33...) - left side, from front to back
  // Q4: descending (48,47,46...) - right side, from back to front
  q1.sort((a, b) => b - a)
  q2.sort((a, b) => a - b)
  q3.sort((a, b) => a - b)
  q4.sort((a, b) => b - a)
  
  const labelStyle: React.CSSProperties = {
    fontSize: '5pt',
    fontWeight: 600,
    color: '#666',
    padding: '0 1px',
  }
  
  const cellStyle: React.CSSProperties = {
    border: '0.5px solid #ccc',
    padding: '1px 3px',
    fontSize: '7pt',
    fontFamily: 'monospace',
    textAlign: 'center',
    minWidth: 20,
  }
  
  return (
    <table style={{ borderCollapse: 'collapse', display: 'inline-table', margin: 0 }}>
      <tbody>
        <tr>
          <td style={cellStyle}>
            <span style={labelStyle}>Q1</span> {q1.length > 0 ? q1.join(',') : '–'}
          </td>
          <td style={cellStyle}>
            <span style={labelStyle}>Q2</span> {q2.length > 0 ? q2.join(',') : '–'}
          </td>
        </tr>
        <tr>
          <td style={cellStyle}>
            <span style={labelStyle}>Q4</span> {q4.length > 0 ? q4.join(',') : '–'}
          </td>
          <td style={cellStyle}>
            <span style={labelStyle}>Q3</span> {q3.length > 0 ? q3.join(',') : '–'}
          </td>
        </tr>
      </tbody>
    </table>
  )
}

// ─── Component ───────────────────────────────────────────────

export default function MonthlyStatementPage() {
  const now = new Date()
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  
  const [selectedMonth, setSelectedMonth] = useState(defaultMonth)
  const [selectedDoctorId, setSelectedDoctorId] = useState('')
  const [selectedDoctor, setSelectedDoctor] = useState<Doctor | null>(null)
  const [showEmailModal, setShowEmailModal] = useState(false)
  const [showBulkSendModal, setShowBulkSendModal] = useState(false)
  const [emailTo, setEmailTo] = useState('')
  const [emailSubject, setEmailSubject] = useState('')
  const [emailMessage, setEmailMessage] = useState('')
  const [selectedDoctorIds, setSelectedDoctorIds] = useState<Set<string>>(new Set())
  const [previewDoctorId, setPreviewDoctorId] = useState<string | null>(null)
  const [isPreviewLoading, setIsPreviewLoading] = useState(false)

  // Handle doctor selection
  const handleDoctorChange = (doctorId: string, doctor?: Doctor) => {
    setSelectedDoctorId(doctorId)
    setSelectedDoctor(doctor || null)
  }

  // Select/Deselect all doctors for bulk send
  const handleSelectAll = () => {
    if (!doctorsWithDues?.doctors) return
    const allIds = new Set(doctorsWithDues.doctors.filter(d => d.email).map(d => d.doctor.id))
    setSelectedDoctorIds(allIds)
  }

  const handleDeselectAll = () => {
    setSelectedDoctorIds(new Set())
  }

  const toggleDoctorSelection = (doctorId: string) => {
    setSelectedDoctorIds(prev => {
      const next = new Set(prev)
      if (next.has(doctorId)) {
        next.delete(doctorId)
      } else {
        next.add(doctorId)
      }
      return next
    })
  }

  // Preview a specific doctor's statement PDF
  const handlePreviewStatement = async (doctorId: string) => {
    setIsPreviewLoading(true)
    setPreviewDoctorId(doctorId)
    try {
      const response = await api.get(`/statements/monthly/${doctorId}/pdf?month=${selectedMonth}`, {
        responseType: 'blob',
      })
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      window.open(url, '_blank')
      setTimeout(() => window.URL.revokeObjectURL(url), 10000)
    } catch {
      toast.error('Failed to load statement preview')
    } finally {
      setIsPreviewLoading(false)
      setPreviewDoctorId(null)
    }
  }

  // Fetch lab profile
  const { data: labProfile } = useQuery({
    queryKey: ['settings', 'lab-profile'],
    queryFn: async () => {
      const { data } = await api.get<{ data: LabProfileResponse | null }>('/settings/lab-profile')
      return data?.data || null
    },
  })

  // Fetch statement data for selected doctor
  const { data: statement, isLoading } = useQuery<DoctorMonthlyStatement>({
    queryKey: ['statements', 'monthly', selectedDoctorId, selectedMonth],
    queryFn: async () => {
      const { data } = await api.get(`/statements/monthly/${selectedDoctorId}?month=${selectedMonth}`)
      return data
    },
    enabled: !!selectedDoctorId,
  })

  // Fetch all doctors with dues for bulk send
  const { data: doctorsWithDues } = useQuery({
    queryKey: ['statements', 'doctors-with-dues', selectedMonth],
    queryFn: async () => {
      const { data } = await api.get(`/statements/doctors-with-dues?month=${selectedMonth}`)
      return data as { doctors: Array<{ doctor: Doctor; balance: number; email: string }> }
    },
  })

  // Send email mutation
  const sendEmailMutation = useMutation({
    mutationFn: async (payload: { doctorId: string; month: string; to: string; subject: string; message: string }) => {
      return api.post(`/statements/monthly/${payload.doctorId}/send-email`, {
        month: payload.month,
        to: payload.to,
        subject: payload.subject,
        message: payload.message,
      })
    },
    onSuccess: () => {
      toast.success('Statement sent successfully')
      setShowEmailModal(false)
    },
    onError: () => {
      toast.error('Failed to send statement')
    },
  })

  // Bulk send mutation
  const bulkSendMutation = useMutation({
    mutationFn: async ({ month, doctorIds }: { month: string; doctorIds: string[] }) => {
      const { data } = await api.post<BulkSendResult>('/statements/bulk-send', { month, doctor_ids: doctorIds })
      return data
    },
    onSuccess: (data) => {
      if (data.failed > 0) {
        toast.error(`Sent ${data.sent} statements, ${data.failed} failed`)
      } else {
        toast.success(`Successfully sent ${data.sent} statements`)
      }
      setShowBulkSendModal(false)
    },
    onError: () => {
      toast.error('Bulk send failed')
    },
  })

  // Get period label
  const getSelectedPeriodLabel = (period: string) => {
    const opt = periodOptions.find(o => o.value === period)
    return opt?.label || period
  }

  // Unified PDF handling - always use backend-generated PDF for consistency
  // This ensures Preview, Print, Download, and Email attachment are identical
  const fetchStatementPDF = async (): Promise<Blob | null> => {
    if (!selectedDoctorId) return null
    try {
      const response = await api.get(`/statements/monthly/${selectedDoctorId}/pdf?month=${selectedMonth}`, {
        responseType: 'blob',
      })
      return new Blob([response.data], { type: 'application/pdf' })
    } catch {
      toast.error('Failed to generate PDF')
      return null
    }
  }

  // Handle print - opens backend PDF in new tab for printing
  const handlePrint = async () => {
    const blob = await fetchStatementPDF()
    if (!blob) return

    const url = window.URL.createObjectURL(blob)
    window.open(url, '_blank')
    // Revoke after delay to allow browser to load the PDF
    setTimeout(() => window.URL.revokeObjectURL(url), 10000)
  }

  // Handle download PDF - uses unified PDF generation
  const handleDownloadPDF = async () => {
    const blob = await fetchStatementPDF()
    if (!blob) return

    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `statement_${(selectedDoctor?.name || statement?.doctor.name || 'doctor').replace(/\s+/g, '_')}_${selectedMonth}.pdf`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
    toast.success('Statement downloaded')
  }

  // Handle email modal open
  const handleOpenEmailModal = () => {
    if (!selectedDoctor) return
    setEmailTo(selectedDoctor.email || '')
    setEmailSubject(`Monthly Statement - ${getSelectedPeriodLabel(selectedMonth)} - ${labProfile?.name || 'Dental Lab'}`)
    setEmailMessage(`Dear ${selectedDoctor.name},\n\nPlease find attached your monthly statement for ${getSelectedPeriodLabel(selectedMonth)}.\n\nIf you have any questions, please don't hesitate to contact us.\n\nThank you for your business.`)
    setShowEmailModal(true)
  }

  // Handle send email
  const handleSendEmail = () => {
    if (!selectedDoctorId || !emailTo) return
    sendEmailMutation.mutate({
      doctorId: selectedDoctorId,
      month: selectedMonth,
      to: emailTo,
      subject: emailSubject,
      message: emailMessage,
    })
  }

  // ─── Render ─────────────────────────────────────────────────

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Monthly Statements</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Generate detailed monthly statements with work order details and notation
            </p>
          </div>
          <div className="flex gap-2 no-print">
            <Button
              variant="secondary"
              onClick={() => {
                // Pre-select all doctors with valid emails
                if (doctorsWithDues?.doctors) {
                  const allIds = new Set(doctorsWithDues.doctors.filter(d => d.email).map(d => d.doctor.id))
                  setSelectedDoctorIds(allIds)
                }
                setShowBulkSendModal(true)
              }}
              disabled={!doctorsWithDues?.doctors?.length}
            >
              <i className="fas fa-paper-plane mr-2"></i>
              Bulk Send ({doctorsWithDues?.doctors?.length || 0})
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <div className="flex flex-col sm:flex-row sm:items-end gap-4">
            <div className="w-full sm:w-80 sm:max-w-sm">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Select Doctor
              </label>
              <DoctorSearchSelect
                value={selectedDoctorId}
                onChange={handleDoctorChange}
                placeholder="Search doctor..."
              />
            </div>
            <div className="w-full sm:w-56">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Statement Period
              </label>
              <SearchableSelect
                value={selectedMonth}
                onChange={(val) => setSelectedMonth(val)}
                placeholder="Select month"
                icon="fa-calendar"
                options={periodOptions}
                minWidth="180px"
              />
            </div>
          </div>
        </Card>

        {/* No doctor selected */}
        {!selectedDoctorId && (
          <Card>
            <div className="text-center py-12">
              <i className="fas fa-file-invoice text-4xl text-gray-400 mb-4"></i>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Select a Doctor</h3>
              <p className="text-gray-500 dark:text-gray-400">
                Choose a doctor from the dropdown above to generate their monthly statement.
              </p>
            </div>
          </Card>
        )}

        {/* Loading */}
        {selectedDoctorId && isLoading && (
          <div className="flex items-center justify-center h-64">
            <Spinner size="lg" />
          </div>
        )}

        {/* Statement Content */}
        {selectedDoctorId && statement && !isLoading && (
          <>
            {/* Doctor Info & Actions */}
            <Card>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {statement.doctor.name}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400">{statement.doctor.clinicName}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {statement.doctor.phone} {statement.doctor.email && `• ${statement.doctor.email}`}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="secondary" onClick={handlePrint}>
                    <i className="fas fa-print mr-2"></i>Print
                  </Button>
                  <Button variant="secondary" onClick={handleDownloadPDF}>
                    <i className="fas fa-download mr-2"></i>Download PDF
                  </Button>
                  <Button variant="secondary" onClick={handleOpenEmailModal} disabled={!statement.doctor.email}>
                    <i className="fas fa-envelope mr-2"></i>Email Statement
                  </Button>
                </div>
              </div>
            </Card>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Card>
                <div className="text-center">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Previous Dues</p>
                  <p className={`text-2xl font-bold ${statement.previousDues > 0 ? 'text-amber-600' : 'text-gray-600'}`}>
                    {formatCurrency(statement.previousDues)}
                  </p>
                </div>
              </Card>
              <Card>
                <div className="text-center">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Current Month</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {formatCurrency(statement.currentMonthTotal)}
                  </p>
                </div>
              </Card>
              <Card>
                <div className="text-center">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Received</p>
                  <p className="text-2xl font-bold text-green-600">
                    {formatCurrency(statement.currentMonthPaid)}
                  </p>
                </div>
              </Card>
              <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
                <div className="text-center">
                  <p className="text-sm text-red-100">Total Balance</p>
                  <p className="text-2xl font-bold">
                    {formatCurrency(statement.totalDue)}
                  </p>
                </div>
              </Card>
            </div>

            {/* Statement Table */}
            <Card padding="none">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <CardHeader
                  title={`Billwise Outstanding Report - ${getSelectedPeriodLabel(selectedMonth)}`}
                  subtitle={`${statement?.items?.length ?? 0} entries`}
                />
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">WO #</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">WO Date</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Bill No</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Bill Date</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Patient</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Product</th>
                      <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Notation</th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Units</th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Net Amount</th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Received</th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Balance</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                    {(statement?.items ?? []).map((item, idx) => (
                      <tr key={`${item.invoiceId}-${idx}`} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="px-3 py-2 text-sm whitespace-nowrap">
                          {item.workOrderId ? (
                            <Link
                              to={`/work-orders/${item.workOrderId}`}
                              className="text-primary-600 hover:underline font-medium"
                            >
                              {item.workOrderNumber || item.workOrderId.slice(0, 8)}
                            </Link>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                          {item.workOrderDate ? formatDate(item.workOrderDate) : '-'}
                        </td>
                        <td className="px-3 py-2 text-sm whitespace-nowrap">
                          <Link
                            to={`/invoices/${item.invoiceId}`}
                            className="text-primary-600 hover:underline font-medium"
                          >
                            {item.invoiceNumber}
                          </Link>
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                          {formatDate(item.billDate)}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">
                          {item.patientName || '-'}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">
                          {item.productName}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <TeethQuadrantGrid notation={item.notation} />
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-900 dark:text-white text-right">
                          {item.units}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-900 dark:text-white text-right font-medium">
                          {formatCurrency(item.netAmount)}
                        </td>
                        <td className="px-3 py-2 text-sm text-green-600 text-right">
                          {formatCurrency(item.received)}
                        </td>
                        <td className="px-3 py-2 text-sm text-right font-semibold">
                          <span className={item.balance > 0 ? 'text-red-600' : 'text-green-600'}>
                            {formatCurrency(item.balance)}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {statement.items.length === 0 && (
                      <tr>
                        <td colSpan={11} className="px-3 py-8 text-center text-gray-500">
                          No records found for this period
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot className="bg-gray-100 dark:bg-gray-800">
                    <tr className="font-semibold">
                      <td colSpan={8} className="px-3 py-3 text-sm text-gray-900 dark:text-white text-right">
                        TOTAL
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-900 dark:text-white text-right">
                        {formatCurrency(statement.summary.totalInvoiced)}
                      </td>
                      <td className="px-3 py-3 text-sm text-green-600 text-right">
                        {formatCurrency(statement.summary.totalPaid)}
                      </td>
                      <td className="px-3 py-3 text-sm text-red-600 text-right">
                        {formatCurrency(statement.summary.totalBalance)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </Card>
          </>
        )}
      </div>

      {/* Email Modal */}
      <Modal isOpen={showEmailModal} onClose={() => setShowEmailModal(false)} title="Email Statement" size="md">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Recipient Email
            </label>
            <input
              type="email"
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="doctor@example.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Subject
            </label>
            <input
              type="text"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Message
            </label>
            <textarea
              value={emailMessage}
              onChange={(e) => setEmailMessage(e.target.value)}
              rows={5}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
            />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" onClick={() => setShowEmailModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendEmail} loading={sendEmailMutation.isPending} disabled={!emailTo}>
              <i className="fas fa-paper-plane mr-2"></i>
              Send Statement
            </Button>
          </div>
        </div>
      </Modal>

      {/* Bulk Send Modal */}
      <Modal isOpen={showBulkSendModal} onClose={() => setShowBulkSendModal(false)} title="Bulk Send Statements" size="lg">
        <div className="space-y-4">
          {/* Confirmation header */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <i className="fas fa-exclamation-triangle text-amber-600 mt-0.5"></i>
              <div>
                <h4 className="font-medium text-amber-800 dark:text-amber-200">
                  Bulk Send Confirmation
                </h4>
                <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                  {selectedDoctorIds.size > 0
                    ? `${selectedDoctorIds.size} of ${doctorsWithDues?.doctors?.length || 0} doctors selected for ${getSelectedPeriodLabel(selectedMonth)}.`
                    : `Select doctors to send statements for ${getSelectedPeriodLabel(selectedMonth)}.`}
                </p>
              </div>
            </div>
          </div>

          {/* Selection Controls */}
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              <Button variant="secondary" onClick={handleSelectAll} className="text-xs px-3 py-1">
                <i className="fas fa-check-double mr-1"></i> Select All
              </Button>
              <Button variant="secondary" onClick={handleDeselectAll} className="text-xs px-3 py-1">
                <i className="fas fa-times mr-1"></i> Deselect All
              </Button>
            </div>
            <span className="text-sm text-gray-500">
              {selectedDoctorIds.size} selected
            </span>
          </div>

          {/* Doctor List with Checkboxes */}
          {doctorsWithDues?.doctors && doctorsWithDues.doctors.length > 0 && (
            <div className="max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 w-10">
                      <input
                        type="checkbox"
                        checked={selectedDoctorIds.size > 0 && selectedDoctorIds.size === doctorsWithDues.doctors.filter(d => d.email).length}
                        onChange={(e) => e.target.checked ? handleSelectAll() : handleDeselectAll()}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Doctor</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Email</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Balance</th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Preview</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {doctorsWithDues.doctors.map((d) => (
                    <tr key={d.doctor.id} className={`${!d.email ? 'opacity-50' : ''}`}>
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selectedDoctorIds.has(d.doctor.id)}
                          onChange={() => toggleDoctorSelection(d.doctor.id)}
                          disabled={!d.email}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">{d.doctor.name}</td>
                      <td className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400">
                        {d.email || <span className="text-red-500 text-xs">No email</span>}
                      </td>
                      <td className="px-3 py-2 text-sm text-right text-red-600 font-medium">
                        {formatCurrency(d.balance)}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <button
                          type="button"
                          onClick={() => handlePreviewStatement(d.doctor.id)}
                          disabled={isPreviewLoading && previewDoctorId === d.doctor.id}
                          className="text-primary-600 hover:text-primary-700 text-xs font-medium disabled:opacity-50"
                        >
                          {isPreviewLoading && previewDoctorId === d.doctor.id ? (
                            <i className="fas fa-spinner fa-spin"></i>
                          ) : (
                            <i className="fas fa-eye"></i>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Send Progress */}
          {bulkSendMutation.isPending && (
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="flex items-center gap-3">
                <i className="fas fa-spinner fa-spin text-blue-600"></i>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  Sending statements... Please wait.
                </p>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" onClick={() => setShowBulkSendModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => bulkSendMutation.mutate({ month: selectedMonth, doctorIds: Array.from(selectedDoctorIds) })}
              loading={bulkSendMutation.isPending}
              disabled={selectedDoctorIds.size === 0}
            >
              <i className="fas fa-paper-plane mr-2"></i>
              Send to Selected ({selectedDoctorIds.size})
            </Button>
          </div>
        </div>
      </Modal>
    </>
  )
}
